/** The root of the taxonomy. Every thrown WeTalk failure is an instance of this. */
export class WeTalkError extends Error {
    code;
    field;
    constructor(options) {
        super(options.message, options.cause === undefined ? undefined : { cause: options.cause });
        this.code = options.code;
        this.field = options.field;
    }
}
/**
 * A required configuration value is absent or invalid (design 9.1). The process refuses to
 * start; it never substitutes a default.
 */
export class ConfigurationError extends WeTalkError {
    name = "ConfigurationError";
    httpStatus = null;
    retryable = false;
    constructor(options) {
        super(options);
    }
}
/** Caller input is wrong. */
export class ValidationError extends WeTalkError {
    name = "ValidationError";
    httpStatus = 400;
    retryable = false;
    constructor(options) {
        super(options);
    }
}
/** No credential, or a bad one. */
export class AuthenticationError extends WeTalkError {
    name = "AuthenticationError";
    httpStatus = 401;
    retryable = false;
    constructor(options) {
        super(options);
    }
}
/** A valid credential with insufficient right. */
export class AuthorizationError extends WeTalkError {
    name = "AuthorizationError";
    httpStatus = 403;
    retryable = false;
    constructor(options) {
        super(options);
    }
}
/**
 * A scoped lookup found nothing — including an id belonging to another account, which is
 * indistinguishable from absence by construction (design 4.2: a forgotten scope yields zero
 * rows, never all rows).
 */
export class NotFoundError extends WeTalkError {
    name = "NotFoundError";
    httpStatus = 404;
    retryable = false;
    constructor(options) {
        super(options);
    }
}
/** Optimistic concurrency or a uniqueness clash. Retryable only after a refetch. */
export class ConflictError extends WeTalkError {
    name = "ConflictError";
    httpStatus = 409;
    retryable = false;
    constructor(options) {
        super(options);
    }
}
/**
 * Well-formed input that cannot be acted on as asked — a request the API understood and refuses on
 * its merits: an export over its row ceiling, a calling window that closes before it opens, a
 * simultaneous-call figure above the tier's ceiling, an unsupported country. Distinct from
 * {@link ValidationError} (400: the input itself is malformed) so a caller can tell "fix the shape
 * of what you sent" from "what you sent is shaped right and still cannot be done".
 *
 * Added 2026-09-22 (plan-002, before Wave 2): six units raise 422 codes (§15.12.2) and each was about
 * to declare its own local class, as the export helper and the pipeline's rate limiter already had.
 */
export class UnprocessableError extends WeTalkError {
    name = "UnprocessableError";
    httpStatus = 422;
    retryable = false;
    constructor(options) {
        super(options);
    }
}
/**
 * The request is well-formed and allowed, and refused because the account cannot pay for it: the
 * live-balance refusals (`credit_exhausted`, `trial_exhausted`, `hard_stop_reached`), and a
 * workspace's spent monthly budget with its hard stop on (`budget_exceeded`, 2026-09-23). Not
 * retryable unchanged — the same request succeeds only after credit is added or the budget is
 * raised, which is a different act, so a client must never loop on it.
 */
export class PaymentRequiredError extends WeTalkError {
    name = "PaymentRequiredError";
    httpStatus = 402;
    retryable = false;
    constructor(options) {
        super(options);
    }
}
/**
 * The request body, or a text it carries, is over the documented byte ceiling (design §15.5.6:
 * `payload_too_large`). Nothing was read or saved. Never retryable unchanged: the same bytes are
 * refused again, and the answer is to split them.
 */
export class PayloadTooLargeError extends WeTalkError {
    name = "PayloadTooLargeError";
    httpStatus = 413;
    retryable = false;
    constructor(options) {
        super(options);
    }
}
/**
 * The thing the request names existed and has lapsed for good: an upload grant past its window
 * (design §16.18: `upload_grant_expired`). Distinct from {@link NotFoundError} (404: there is, or
 * may as well be, nothing) so a client can tell "ask for a new one" from "that never existed". Not
 * retryable unchanged — the same grant is refused again; the answer is a fresh one.
 */
export class GoneError extends WeTalkError {
    name = "GoneError";
    httpStatus = 410;
    retryable = false;
    constructor(options) {
        super(options);
    }
}
/**
 * The request declares a content type the platform does not read (design §16.18:
 * `document_type_unsupported`). Distinct from {@link ValidationError} (400: malformed) — the
 * declaration is well-formed and names a type outside the accepted set. Never retryable unchanged.
 */
export class UnsupportedMediaTypeError extends WeTalkError {
    name = "UnsupportedMediaTypeError";
    httpStatus = 415;
    retryable = false;
    constructor(options) {
        super(options);
    }
}
/**
 * Nothing is there to serve this right now — no agent runtime is deployed, a provider is not yet
 * bound, the process is draining. Distinct from {@link UpstreamError} (502/504), which says a
 * dependency answered badly or too late: here it did not answer because it is not there.
 * Retryable later; a route sends no `Retry-After` unless it can back the date it names.
 */
export class ServiceUnavailableError extends WeTalkError {
    name = "ServiceUnavailableError";
    httpStatus = 503;
    retryable = true;
    constructor(options) {
        super(options);
    }
}
/**
 * The call cannot be attributed to a VoiceAgent (design 9.3). The call is rejected; there is
 * no default VoiceAgent and no platform fallback menu.
 */
export class TenantResolutionError extends WeTalkError {
    name = "TenantResolutionError";
    httpStatus = null;
    retryable = false;
    subject;
    constructor(options) {
        super(options);
        this.subject = options.subject;
    }
}
/**
 * A language pack or an acknowledgement set is incomplete, so that language is unavailable
 * (design 9.4). The missing deliverable is named — there is no English fallback.
 */
export class LanguageUnavailableError extends WeTalkError {
    name = "LanguageUnavailableError";
    httpStatus = 422;
    retryable = false;
    /** The language that is unavailable, as a BCP-47 code from the 26-language set. */
    language;
    /** Which of design 9.4's requirements is unmet. */
    missing;
    /** The specific key or deliverable, when the class alone is not enough to act on. */
    detail;
    constructor(options) {
        super(options);
        this.language = options.language;
        this.missing = options.missing;
        this.detail = options.detail;
    }
}
/** A vendor failed. Bounded retry is allowed. */
export class UpstreamError extends WeTalkError {
    name = "UpstreamError";
    httpStatus;
    retryable = true;
    /** The vendor, as WeTalk names it — never the vendor's own noun for itself. */
    upstream;
    constructor(options) {
        super(options);
        this.upstream = options.upstream;
        this.httpStatus = options.timedOut ? 504 : 502;
    }
}
/**
 * A side effect may or may not have happened (design 7.7, 7.10). The only correct response
 * is to go and look up what happened; never to retry blindly.
 */
export class AmbiguousOutcomeError extends WeTalkError {
    name = "AmbiguousOutcomeError";
    httpStatus = null;
    retryable = false;
    /** The deterministic reference the lookup is keyed by (`merchant_ref`, idempotency key). */
    resolveBy;
    constructor(options) {
        super(options);
        this.resolveBy = options.resolveBy;
    }
}
/** Narrows an unknown thrown value to the taxonomy. */
export function isWeTalkError(value) {
    return value instanceof WeTalkError;
}
//# sourceMappingURL=taxonomy.js.map